import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { YoutubeVideoPreview } from '../../../../app/components/YoutubeVideo';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../app/components/SnackBar/SnackBar';
import { mentorBookPath, paths } from '../../../../app/routes/paths';
import { useRolePath } from '../../../../app/routes/useRolePath';
import { useAppSelector } from '../../../../app/store/hooks';
import { timezoneOptionLabel } from '../../../../helpers/timezone.helper';
import { formatLessonAmount } from '../../../mentor/application/formatLessonPrice';
import { PictureByGender } from '../../../user/ui/components/PictureByGender';
import { useGetPublicMentorBySlugQuery } from '../../infrastructure/api/public-profile.api';
import './PublicMentorProfilePage.scss';

function filled(value: string | null | undefined): string | null {
  const text = value?.trim() ?? '';
  if (!text) return null;
  return text;
}

function yearsKey(years: number, language: string): string {
  const english = !language.toLowerCase().startsWith('ua');
  if (english) return years === 1 ? 'public_years_one' : 'public_years';
  const mod10 = years % 10;
  const mod100 = years % 100;
  if (mod10 === 1 && mod100 !== 11) return 'public_years_one';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
    return 'public_years_few';
  return 'public_years';
}

export const PublicMentorProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const to = useRolePath();
  const accessToken = useAppSelector((state) => state.authorization.accessToken);
  const { data, isLoading, isError } = useGetPublicMentorBySlugQuery(slug, {
    skip: !slug,
  });
  const profile = data?.profile;

  if (isLoading) {
    return (
      <div className="public-mentor-profile public-mentor-profile--state">
        <Spinner />
      </div>
    );
  }

  if (isError || !profile?.displayName) {
    return (
      <div className="public-mentor-profile public-mentor-profile--state">
        <p className="public-mentor-profile__missing">
          {t('public-profile.not_found')}
        </p>
      </div>
    );
  }

  const about = filled(profile.description);
  const goals = filled(profile.goals);
  const headline = filled(profile.headline);
  const introVideo = filled(profile.introVideoUrl);
  const timezone =
    timezoneOptionLabel(profile.timezone) || filled(profile.timezone);
  const languages = (profile.nativeLanguages ?? [])
    .map((code) => t(`mentor-profile.lang.${code}`))
    .filter((label) => label && !label.startsWith('mentor-profile.lang.'));
  const categories = (data.categories ?? []).filter((item) =>
    filled(item.title),
  );
  const priceLabel = data.price
    ? t('mentor-profile.price_line', {
        amount: formatLessonAmount(data.price, i18n.language),
        duration: data.price.durationMinutes,
      })
    : null;
  const years =
    profile.experienceYears != null
      ? t(`mentor-profile.${yearsKey(profile.experienceYears, i18n.language)}`, {
          years: profile.experienceYears,
        })
      : null;
  const hasContent = Boolean(about || goals || categories.length);

  async function shareProfile() {
    const path =
      data?.publicPath || `/public/mentor/${data?.slug || slug}`;
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      showSuccessToast(t('mentor-profile.link_copied'));
    } catch {
      showErrorToast(t('mentor-profile.link_copy_failed'));
    }
  }

  return (
    <div className="public-mentor-profile">
      <aside className="public-mentor-profile__aside">
        <div className="public-mentor-profile__avatar">
          <PictureByGender picture={profile.avatarUrl} gender={null} />
        </div>
        <h1 className="public-mentor-profile__name">{profile.displayName}</h1>
        {headline ? (
          <p className="public-mentor-profile__headline">{headline}</p>
        ) : null}
        {years ? (
          <p className="public-mentor-profile__years">{years}</p>
        ) : null}

        {introVideo ? (
          <div className="public-mentor-profile__video">
            <YoutubeVideoPreview
              url={introVideo}
              title={t('mentor-profile.intro_video_title')}
            />
          </div>
        ) : null}

        {priceLabel ? (
          <p className="public-mentor-profile__price">{priceLabel}</p>
        ) : null}

        {languages.length > 0 ? (
          <div className="public-mentor-profile__meta">
            <h2>{t('mentor-profile.steps.languages')}</h2>
            <p>{languages.join(' · ')}</p>
          </div>
        ) : null}

        {timezone ? (
          <div className="public-mentor-profile__meta">
            <h2>{t('mentor-profile.timezone')}</h2>
            <p>{timezone}</p>
          </div>
        ) : null}

        <div className="public-mentor-profile__actions">
          <button
            type="button"
            className="public-mentor-profile__book"
            onClick={() => {
              if (accessToken) {
                navigate(to(mentorBookPath(data.slug || slug)));
                return;
              }
              navigate(paths.signIn);
            }}
          >
            {t('mentor-profile.book_lesson')}
          </button>
          <button
            type="button"
            className="public-mentor-profile__share"
            onClick={() => {
              void shareProfile();
            }}
          >
            {t('mentor-profile.share_profile')}
          </button>
        </div>
      </aside>

      {hasContent ? (
        <div className="public-mentor-profile__main">
          {about ? (
            <section className="public-mentor-profile__section">
              <h2>{t('mentor-profile.about')}</h2>
              <p>{about}</p>
            </section>
          ) : null}
          {goals ? (
            <section className="public-mentor-profile__section">
              <h2>{t('mentor-profile.goals')}</h2>
              <p>{goals}</p>
            </section>
          ) : null}
          {categories.length > 0 ? (
            <section className="public-mentor-profile__section">
              <h2>{t('mentor-profile.categories')}</h2>
              <ul className="public-mentor-profile__chips">
                {categories.map((category) => (
                  <li key={category.id}>{category.title}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default PublicMentorProfilePage;
